import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { showToast, handleApiError, RateLimitInfo } from '../utils/errorHandler';
import { eventBus } from '../utils/eventBus';
import RateLimitNotification from '../components/RateLimitNotification';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import { RecruiterPageShell } from '../components/Recruiter/RecruiterStates';
import CandidateInboxCard from '../components/RecruiterInbox/CandidateInboxCard';
import CVViewerModal from '../components/RecruiterInbox/CVViewerModal';
import { 
  MessageCircle, 
  Search, 
  User, 
  Briefcase,
  Building,
  Calendar,
  Send,
  Star,
  Smile,
  ArrowLeft,
  Filter,
  ChevronDown,
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { useTranslation } from 'react-i18next';

// Types
interface User {
  id: string;
  token: string;
  name: string;
  subscription_type: string;
  is_recruiter: boolean;
  icon: React.ReactNode;
  color: string;
}

interface JobCluster {
  name: string;
  count: number;
  jobs: Array<{
    id: string;
    metadata: {
      cv_id: string;
      candidate_id: string;
      user_id: string; // Changed from clerk_user_id to user_id (UUID)
      job_title: string;
      status: string;
      conversation_id: string;
      timestamp: number;
      last_message_at?: number; // Optional: Unix timestamp of last message
    };
  }>;
}

interface Conversation {
  id: string;
  messages: Array<{
    id: string;
    content: string;
    created_at: number;  // Unix timestamp
    sender: {
      user_id: string; // Changed from clerk_user_id to user_id (UUID)
    };
  }>;
}

interface InboxData {
  clusters: JobCluster[];
  conversations: Record<string, Conversation>;
}

const RecruiterInbox: React.FC = () => {
  const { user, apiCall: customApiCall, isSignedIn, isLoading: authLoading } = useAuth(); // Custom hook with auto-retry
  const { t } = useTranslation('recruiterInbox');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationParam = searchParams.get('conversation');
  // Note: Real-time updates come via EventBus from InboxWidget SSE connection
  
  // State
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [inboxData, setInboxData] = useState<InboxData | null>(null);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [conversationMessages, setConversationMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [unreadByConversation, setUnreadByConversation] = useState<Record<string, number>>({});
  const [messagePreviewsByConversation, setMessagePreviewsByConversation] = useState<Record<string, string>>({});
  const [messageSendersByConversation, setMessageSendersByConversation] = useState<Record<string, string>>({});
  const [subjectsByConversation, setSubjectsByConversation] = useState<Record<string, string>>({});
  const [salaryRangeByConversation, setSalaryRangeByConversation] = useState<Record<string, string>>({});
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [niceToKnowByConversation, setNiceToKnowByConversation] = useState<Record<string, string>>({});
  const [candidateProfiles, setCandidateProfiles] = useState<Record<string, any>>({});
  const [loadingProfiles, setLoadingProfiles] = useState<Record<string, boolean>>({});
  
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCVModal, setShowCVModal] = useState(false);
  const [selectedCandidateForCV, setSelectedCandidateForCV] = useState<{candidateId: string, cvId: string, name?: string} | null>(null);

  // Emit total unread count whenever unreadByConversation changes
  useEffect(() => {
    const total = Object.values(unreadByConversation).reduce((a, b) => a + (b || 0), 0);
    eventBus.emit('messaging:total_unread', { total_unread: total });
  }, [unreadByConversation]);

  // API Base URL from environment variable
  const API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/v1`;

  // Scroll to bottom of messages (only within chat container, not globally)
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Wrapper to make custom apiCall compatible with existing code
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const fullUrl = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    const response = await customApiCall(fullUrl, options);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'API Error' }));
      console.error('❌ RecruiterInbox API Error:', {
        status: response.status,
        statusText: response.statusText,
        error: error
      });
      throw new Error(error.detail || error.message || `API Error: ${response.status}`);
    }
    
    return response.json();
  };

  // Load current user profile
  const loadUserProfile = async () => {
    if (!user?.userId) return;
    
    try {
      console.debug('🔗 RecruiterInbox: Loading user profile via Gateway');
      const profile = await apiCall(`/profiles/${user.userId}`);
      
      // ❌ Redirect explorers to messages (silent redirect, no toast)
      if (!profile.is_recruiter) {
        navigate('/messages');
        return;
      }
      
      setCurrentUser({
        id: profile.id, // Use UUID instead of Clerk ID
        token: '',
        name: profile.first_name || 'Recruiter',
        subscription_type: profile.subscription_type,
        is_recruiter: profile.is_recruiter,
        icon: <Building className="w-4 h-4" />,
        color: 'text-pink-600'
      });
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // Fallback to basic user info
      setCurrentUser({
        id: user.userId, // Keep Clerk ID as fallback
        token: '',
        name: 'Recruiter',
        subscription_type: 'recruiter_premium',
        is_recruiter: true,
        icon: <Building className="w-4 h-4" />,
        color: 'text-pink-600'
      });
    }
  };

  // Refresh candidates when SSE reconnects (to catch missed messages) - silent, no loading spinner
  useEffect(() => {
    const unsubscribe = eventBus.on('messaging:refresh_needed', () => {
      console.debug('🔄 RecruiterInbox: Refreshing candidates after SSE reconnect (silent)');
      if (currentUser) {
        loadInboxData(true); // silent refresh
        
        // Also refresh messages in currently open chat
        if (selectedJob?.metadata?.conversation_id) {
          console.debug('🔄 RecruiterInbox: Refreshing open chat after SSE reconnect');
          loadMessages(selectedJob.metadata.conversation_id);
        }
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, selectedJob]);

  // Subscribe to SSE events from InboxWidget
  useEffect(() => {
    const unsubscribe = eventBus.on('messaging:new_message', (data: any) => {
      console.debug('📨 RecruiterInbox: Received SSE message:', data);
      
      // Check if this message is for the currently open conversation
      if (selectedJob?.metadata?.conversation_id === data.conversation_id) {
        console.debug('📨 RecruiterInbox: Message is for open conversation, appending...');
        
        // Append message to current conversation
        setConversationMessages(prev => {
          const newMessage = {
            id: data.message.id,
            content: data.message.content,
            created_at: data.message.created_at,
            sender: data.message.sender
          };
          
          // Check if message already exists (avoid duplicates)
          const exists = prev.some(msg => msg.id === newMessage.id);
          if (exists) return prev;
          
          const updated = [...prev, newMessage];
          
          // Auto-scroll to bottom
          setTimeout(() => scrollToBottom(), 100);
          
          return updated;
        });
      } else {
        // Update unread count for other conversations (only if from others)
        // NOTE: Don't increment here - InboxWidget handles global unread count
        const fromUserId = data?.message?.sender?.user_id;
        if (fromUserId && currentUser && fromUserId !== currentUser.id && data?.conversation_id) {
          const cid = data.conversation_id;
          // Just mark as having unread, don't increment (InboxWidget handles the count)
          setUnreadByConversation(prev => ({
            ...prev,
            [cid]: Math.max(prev[cid] || 0, 1) // Ensure it's at least 1, don't increment
          }));
          
          // Update message preview for real-time updates
          if (data?.message?.content) {
            setMessagePreviewsByConversation(prev => ({
              ...prev,
              [cid]: data.message.content.substring(0, 300)
            }));
          }
          
          // Update job timestamp and re-sort cluster
          setInboxData(prev => {
            if (!prev?.clusters) return prev;
            
            const newTimestamp = Math.floor(Date.now() / 1000);
            const updatedClusters = prev.clusters.map(cluster => {
              const jobIndex = cluster.jobs.findIndex(
                j => j.metadata?.conversation_id === cid
              );
              
              if (jobIndex === -1) return cluster;
              
              // Update timestamp and sort jobs by last_message_at
              const updatedJobs = [...cluster.jobs];
              updatedJobs[jobIndex] = {
                ...updatedJobs[jobIndex],
                metadata: {
                  ...updatedJobs[jobIndex].metadata,
                  last_message_at: newTimestamp
                }
              };
              
              // Sort by last_message_at (newest first)
              updatedJobs.sort((a, b) => {
                const timeA = a.metadata?.last_message_at || 0;
                const timeB = b.metadata?.last_message_at || 0;
                return timeB - timeA;
              });
              
              return { ...cluster, jobs: updatedJobs };
            });
            
            return { ...prev, clusters: updatedClusters };
          });
        }
        
        // Note: No need to reload inbox for new conversations
        // Recruiter inbox only shows reach-outs initiated by recruiter
        // New conversations don't appear via SSE (they're created by recruiter actions)
      }
    });

    return () => unsubscribe();
  }, [selectedJob?.metadata?.conversation_id, currentUser]);

  // Load user profile when signed in (with guard to prevent duplicate calls)
  useEffect(() => {
    const loadProfileWhenReady = async () => {
      if (!isSignedIn || !user) {
        return;
      }

      // Avoid duplicate attempts
      if (currentUser || hasAttemptedLoad) {
        return;
      }

      setHasAttemptedLoad(true);
      loadUserProfile();
    };

    loadProfileWhenReady();
  }, [isSignedIn, user?.userId]);

  const loadInboxData = async (silent = false) => {
    if (!currentUser) return;
    
    if (!silent) setIsLoading(true);
    try {
      console.debug('🔄 Loading inbox for recruiter:', currentUser.name, silent ? '(silent refresh)' : '');
      const data = await apiCall(`/recruiter/inbox/${currentUser.id}`);
      console.debug('📋 Inbox data:', data);
      console.debug('🕒 Last message times:', data.last_message_times);
      
      // Inject last_message_at into jobs and sort by it
      if (data.last_message_times && data.clusters) {
        data.clusters = data.clusters.map((cluster: any) => ({
          ...cluster,
          jobs: cluster.jobs.map((job: any) => {
            const convId = job.metadata?.conversation_id;
            const lastMsgTime = data.last_message_times[convId];
            const reachOutDate = job.metadata?.timestamp; // Pinecone uses 'timestamp', not 'reach_out_date'
            const finalTime = lastMsgTime || reachOutDate || 0;
            console.debug(`Job ${convId?.slice(0, 20)}: last_msg=${lastMsgTime}, reach_out=${reachOutDate}, using=${finalTime}`);
            return {
              ...job,
              metadata: {
                ...job.metadata,
                last_message_at: finalTime
              }
            };
          }).sort((a: any, b: any) => {
            // Sort by last_message_at (newest first)
            const timeA = a.metadata?.last_message_at || 0;
            const timeB = b.metadata?.last_message_at || 0;
            return timeB - timeA;
          })
        }));
        
        // Log final sorted order
        console.debug('✅ Jobs sorted by last_message_at. Final order:');
        data.clusters.forEach((cluster: any, clusterIdx: number) => {
          console.debug(`Cluster ${clusterIdx}:`);
          cluster.jobs.forEach((job: any, idx: number) => {
            const convId = job.metadata?.conversation_id?.slice(0, 20);
            const time = job.metadata?.last_message_at;
            console.debug(`  ${idx + 1}. ${convId} - time: ${time}`);
          });
        });
      }
      
      setInboxData(data);
      console.debug(`✅ Loaded ${data.clusters?.length || 0} job clusters`);
      
      // Use unread counts from inbox response (already loaded!)
      if (data.unread_counts) {
        setUnreadByConversation(data.unread_counts);
        console.debug(`✅ Loaded unread counts for ${Object.keys(data.unread_counts).length} conversations`);
      }
      
      // Use message previews from inbox response
      if (data.message_previews) {
        setMessagePreviewsByConversation(data.message_previews);
        console.debug(`✅ Loaded message previews for ${Object.keys(data.message_previews).length} conversations`);
      }
      
      // Use message senders from inbox response
      if (data.message_senders) {
        setMessageSendersByConversation(data.message_senders);
        console.debug(`✅ Loaded message senders for ${Object.keys(data.message_senders).length} conversations`);
      }
      
      // Use subjects from inbox response (privacy-friendly display!)
      if (data.subjects) {
        setSubjectsByConversation(data.subjects);
        console.debug(`✅ Loaded subjects for ${Object.keys(data.subjects).length} conversations`);
      }
      
      // Use salary ranges from inbox response
      if (data.salary_ranges) {
        setSalaryRangeByConversation(data.salary_ranges);
        console.debug(`✅ Loaded salary ranges for ${Object.keys(data.salary_ranges).length} conversations`);
      }
      
      // Use nice_to_know from inbox response
      if (data.nice_to_knows) {
        setNiceToKnowByConversation(data.nice_to_knows);
        console.debug(`✅ Loaded nice_to_know for ${Object.keys(data.nice_to_knows).length} conversations`);
      }
    } catch (error: any) {
      console.error('❌ Failed to load inbox:', error);
      setInboxData(null);
      
      // Show user-friendly error
      if (error.message && error.message.includes('429')) {
        showToast('Too many requests. Please wait and try again.', 'error', 6000);
      } else if (error.message && error.message.includes('503')) {
        showToast('Inbox service temporarily unavailable.', 'error');
      } else {
        showToast('Failed to load inbox. Please refresh the page.', 'error');
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  // Load inbox data when user is available
  useEffect(() => {
    if (currentUser) {
      loadInboxData();
    }
  }, [currentUser]);

  // When ?conversation=xxx is in URL and inbox is loaded, auto-select that conversation
  useEffect(() => {
    if (!conversationParam || !inboxData?.clusters) return;
    for (const cluster of inboxData.clusters) {
      const job = cluster.jobs?.find((j: any) => j.metadata?.conversation_id === conversationParam);
      if (job) {
        setSelectedJob(job);
        loadMessages(conversationParam);
        setSearchParams((p) => {
          p.delete('conversation');
          return p;
        }, { replace: true });
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxData, conversationParam]);

  // Load messages for conversation
  const loadMessages = async (conversationId: string) => {
    setIsLoadingMessages(true);
    try {
      console.debug('🔄 Loading messages for conversation:', conversationId);
      const encodedConversationId = encodeURIComponent(conversationId);
      const response = await apiCall(`/messaging/conversations/${encodedConversationId}/messages?limit=100`);
      console.debug('📞 Messages loaded:', response.messages?.length || 0);
      // Reverse messages to show chronological order (oldest first)
      setConversationMessages(response.messages?.reverse() || []);
      // Mark conversation as read and reset unread counter
      try {
        await apiCall(`/messaging/conversations/${encodedConversationId}/read`, { method: 'POST' });
        setUnreadByConversation(prev => ({
          ...prev,
          [conversationId]: 0
        }));
      } catch (e) {
        console.warn('⚠️ Failed to mark conversation as read:', e);
      }
      
      // Auto-scroll to bottom
      setTimeout(() => scrollToBottom(), 100);
    } catch (error: any) {
      console.error('❌ Failed to load messages:', error);
      setConversationMessages([]);
      
      // Show user-friendly error (less intrusive, user can retry by clicking conversation again)
      if (error.message && error.message.includes('429')) {
        showToast('Too many requests. Please wait.', 'error', 6000);
      } else {
        showToast('Failed to load messages. Please try again.', 'error');
      }
    } finally {
      setIsLoadingMessages(false);
    }
  };

  // Send message
  const handleEmojiClick = (emojiData: any) => {
    setNewMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleViewCV = (candidateId: string) => {
    // Get the current profile to fetch the latest cv_id
    const profile = candidateProfiles[candidateId];
    
    console.debug('🔍 handleViewCV called for candidateId:', candidateId);
    console.debug('📋 Profile data:', profile);
    console.debug('📄 cv_id from profile:', profile?.cv_id);
    
    if (profile?.cv_id) {
      const candidateName = profile?.first_name && profile?.last_name 
        ? `${profile.first_name} ${profile.last_name}`
        : profile?.first_name || undefined;
      
      console.debug('✅ Opening CV modal with cv_id:', profile.cv_id);
      
      setSelectedCandidateForCV({
        candidateId,
        cvId: profile.cv_id,  // Use current cv_id from profile, not old one from reach-out
        name: candidateName
      });
      setShowCVModal(true);
    } else {
      console.error('❌ No cv_id found in profile!');
      showToast(t('errors.cvNotAvailable'), 'error');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedJob?.metadata?.conversation_id || sendingMessage) return;
    
    const conversationId = selectedJob.metadata.conversation_id; // Store conversation ID
    const encodedConversationId = encodeURIComponent(conversationId);
    setSendingMessage(true);
    try {
      console.debug('📤 RecruiterInbox: Sending message:', newMessage);
      const response = await apiCall(`/messaging/conversations/${encodedConversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          content: newMessage.trim(),
          type: 'text'
        })
      });
      
      console.debug('✅ RecruiterInbox: Message sent:', response);
      // Optimistically append to current conversation (SSE excludes sender)
      setConversationMessages(prev => {
        const exists = prev.some(m => m.id === response.id);
        if (exists) return prev;
        const appended = [
          ...prev,
          {
            id: response.id,
            content: response.content,
            created_at: response.created_at,
            sender: response.sender
          }
        ];
        // Scroll to bottom after appending
        setTimeout(() => scrollToBottom(), 50);
        return appended;
      });
      setNewMessage('');
      
      // Don't reload - SSE will handle real-time updates
      // await loadMessages(conversationId);
      
      // Auto-focus input after sending
      setTimeout(() => {
        const input = document.querySelector('input[placeholder="Type your message to the candidate..."]') as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 100);
    } catch (error) {
      console.error('❌ RecruiterInbox: Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStatusUpdate = async (vectorId: string, newStatus: string) => {
    try {
      console.debug(`🔄 Updating status for ${vectorId} to ${newStatus}`);
      
      await apiCall('/recruiter/reach-out/status', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vector_id: vectorId,
          status: newStatus
        })
      });
      
      // Update local state
      setInboxData(prev => {
        if (!prev) return prev;
        
        return {
          ...prev,
          clusters: prev.clusters.map(cluster => ({
            ...cluster,
            jobs: cluster.jobs.map(job => 
              job.id === vectorId 
                ? { ...job, metadata: { ...job.metadata, status: newStatus } }
                : job
            )
          }))
        };
      });
      
      console.debug(`✅ Status updated successfully`);
    } catch (error) {
      console.error('❌ Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Fetch all candidate profiles when inbox data loads
  useEffect(() => {
    if (inboxData?.clusters) {
      const allCandidateIds = inboxData.clusters.flatMap(c => 
        c.jobs.map(j => j.metadata.candidate_id)
      );
      if (allCandidateIds.length > 0) {
        fetchCandidateProfiles(allCandidateIds);
      }
    }
  }, [inboxData]);

  const fetchCandidateProfiles = async (candidateIds: string[]) => {
    // Filter out already loaded profiles
    const idsToFetch = candidateIds.filter(id => !candidateProfiles[id] && !loadingProfiles[id]);
    
    if (idsToFetch.length === 0) return;

    // Mark as loading
    const newLoadingState = { ...loadingProfiles };
    idsToFetch.forEach(id => newLoadingState[id] = true);
    setLoadingProfiles(newLoadingState);

    console.debug(`📋 Fetching ${idsToFetch.length} candidate profiles...`);

    try {
      const response = await apiCall('/profiles/batch', {
        method: 'POST',
        body: JSON.stringify({ user_ids: idsToFetch })
      });

      console.debug(`✅ Loaded ${response.found} profiles, ${response.not_found?.length || 0} not found`);

      // Store profiles
      const newProfiles = { ...candidateProfiles };
      response.profiles.forEach((profile: any) => {
        console.debug(`📋 Profile ${profile.id}: cv_id=${profile.cv_id}, name=${profile.first_name} ${profile.last_name}`);
        newProfiles[profile.id] = profile;
      });
      
      // Store placeholder for not found profiles to prevent infinite loading
      if (response.not_found && response.not_found.length > 0) {
        response.not_found.forEach((id: string) => {
          newProfiles[id] = null; // null indicates profile not found/deleted
        });
      }
      
      setCandidateProfiles(newProfiles);

      // Clear loading state
      const clearedLoadingState = { ...loadingProfiles };
      idsToFetch.forEach(id => delete clearedLoadingState[id]);
      setLoadingProfiles(clearedLoadingState);
    } catch (error) {
      console.error('❌ Failed to fetch candidate profiles:', error);
      // Clear loading state on error
      const clearedLoadingState = { ...loadingProfiles };
      idsToFetch.forEach(id => delete clearedLoadingState[id]);
      setLoadingProfiles(clearedLoadingState);
    }
  };

  const selectJob = async (job: any) => {
    setSelectedJob(job);
    
    // Load messages if conversation exists
    if (job.metadata?.conversation_id) {
      console.debug('🔄 Loading messages for conversation:', job.metadata.conversation_id);
      await loadMessages(job.metadata.conversation_id);
    } else {
      console.debug('❌ No conversation ID found for job');
      setConversationMessages([]);
    }
  };

  // Neutral B2B status meta — single muted dot, no emoji/rainbow chips.
  const statusOptions = [
    { value: 'new', label: t('statusLabels.new'), dot: 'bg-slate-300' },
    { value: 'candidate_sparked', label: t('statusLabels.candidate_sparked'), dot: 'bg-violet-400' },
    { value: 'in_talks', label: t('statusLabels.in_talks'), dot: 'bg-blue-400' },
    { value: 'initial_call_scheduled', label: t('statusLabels.initial_call_scheduled'), dot: 'bg-cyan-400' },
    { value: 'interviewing', label: t('statusLabels.interviewing'), dot: 'bg-amber-400' },
    { value: 'offer_talks', label: t('statusLabels.offer_talks'), dot: 'bg-orange-400' },
    { value: 'hired', label: t('statusLabels.hired'), dot: 'bg-emerald-500' },
    { value: 'currently_not_best_fit', label: t('statusLabels.currently_not_best_fit'), dot: 'bg-slate-400' }
  ];

  // Flatten all jobs from clusters into a single list and filter by search query and status
  const filteredJobs = inboxData?.clusters
    .flatMap(cluster => cluster.jobs)
    .filter(job => {
      if (statusFilter !== 'all' && job.metadata.status !== statusFilter) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const title = job.metadata.job_title?.toLowerCase() || '';
      const profile = candidateProfiles[job.metadata.candidate_id];
      const name = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim().toLowerCase();
      const subject = subjectsByConversation[job.metadata.conversation_id]?.toLowerCase() || '';
      return title.includes(q) || name.includes(q) || subject.includes(q);
    })
    .sort((a, b) => (b.metadata?.last_message_at || 0) - (a.metadata?.last_message_at || 0))
  || [];

  // Signed out (auth resolved, no session) → shared recruiter gate.
  if (!authLoading && !isSignedIn) {
    return <RecruiterLoggedOutGate returnTo="/recruiter-inbox" />;
  }

  // Show loading if user not loaded yet
  if (!currentUser) {
    return (
      <RecruiterPageShell noPadding contentClassName="flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-3xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="relative overflow-hidden border-b border-slate-100 p-4">
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
            <div className="h-6 w-40 rounded bg-slate-200 animate-pulse" />
          </div>
          <div className="divide-y divide-slate-100">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="relative flex items-center gap-3 overflow-hidden p-4">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
                <div className="size-10 shrink-0 rounded-full bg-slate-200 animate-pulse" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="h-4 w-32 rounded bg-slate-200 animate-pulse" />
                  <div className="h-3.5 w-56 rounded bg-slate-100 animate-pulse" />
                </div>
                <div className="h-5 w-14 shrink-0 rounded-full bg-slate-100 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </RecruiterPageShell>
    );
  }

  return (
    <RecruiterPageShell fullBleed noPadding>
      {rateLimitInfo && (
        <RateLimitNotification
          rateLimitInfo={rateLimitInfo}
          feature="reach_outs"
          onClose={() => setRateLimitInfo(null)}
        />
      )}

      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-6">
        <div className="lg:grid lg:grid-cols-12 lg:gap-6" style={{ height: 'calc(100vh - 96px)', maxHeight: 'calc(100vh - 96px)' }}>
          
          {/* Left Sidebar - Job Clusters */}
          <div className={`${selectedJob ? 'hidden lg:flex' : 'flex'} lg:col-span-5 flex-col overflow-hidden min-h-0 h-full`}>
            <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm min-h-0">

              {/* Toolbar — status + search (Pipeline-style B2B) */}
              <div className="border-b border-slate-100 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="appearance-none pl-8 pr-8 py-1.5 text-sm font-medium bg-white border border-slate-200 rounded-lg text-slate-700 hover:border-slate-300 focus:outline-none focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 cursor-pointer"
                    >
                      <option value="all">{t('filters.allStatuses')}</option>
                      {statusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                    <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  <div className="relative flex-1 min-w-[140px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('search.placeholder')}
                      className="w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg text-slate-700 placeholder:text-slate-400 hover:border-slate-300 focus:outline-none focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 transition-colors"
                      maxLength={200}
                    />
                  </div>

                  <div className="text-sm text-slate-500 sm:ml-auto">
                    <span className="font-semibold text-slate-700">{filteredJobs.length}</span>{' '}
                    {t('filters.resultCount')}
                  </div>
                </div>
              </div>

              <div className="space-y-2 flex-1 overflow-y-auto min-h-0 p-2.5">
                {isLoading ? (
                  <div className="space-y-2 px-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-2.5 p-2 sm:p-2.5 rounded-lg relative overflow-hidden">
                        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-slate-200 animate-pulse flex-shrink-0" />
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="h-3 sm:h-3.5 w-20 sm:w-28 bg-slate-200 rounded animate-pulse" />
                          <div className="h-2.5 sm:h-3 w-32 sm:w-40 bg-slate-100 rounded animate-pulse" />
                        </div>
                        <div className="h-4 w-12 bg-slate-100 rounded-full animate-pulse flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                ) : filteredJobs.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageCircle className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                    <p className="text-sm text-slate-600">{t('candidateList.noCandidates')}</p>
                    <p className="text-xs text-slate-400">{t('candidateList.noCandidatesHint')}</p>
                  </div>
                ) : (
                  filteredJobs.map((job) => {
                    const candidateId = job.metadata.candidate_id;
                    const profile = candidateProfiles[candidateId];
                    const conversationId = job.metadata.conversation_id;
                    const unreadCount = unreadByConversation[conversationId] || 0;
                    
                    // Get last message preview, sender, and subject from backend (loaded with inbox data)
                    const lastMessagePreview: string | undefined = messagePreviewsByConversation[conversationId];
                    const lastMessageSenderId: string | undefined = messageSendersByConversation[conversationId];
                    const subject: string | undefined = subjectsByConversation[conversationId];
                    const salaryRange: string | undefined = salaryRangeByConversation[conversationId];
                    const niceToKnow: string | undefined = niceToKnowByConversation[conversationId];

                    return (
                      <CandidateInboxCard
                        key={job.id}
                        candidateId={candidateId}
                        profile={profile}
                        jobTitle={job.metadata.job_title}
                        subject={subject}
                        timestamp={job.metadata.timestamp}
                        status={job.metadata.status}
                        conversationId={conversationId}
                        unreadCount={unreadCount}
                        lastMessagePreview={lastMessagePreview}
                        lastMessageSenderId={lastMessageSenderId}
                        currentUserId={currentUser?.id}
                        lastMessageAt={job.metadata.last_message_at}
                        isSelected={selectedJob?.id === job.id}
                        onClick={() => selectJob(job)}
                        onStatusChange={(newStatus) => handleStatusUpdate(job.id, newStatus)}
                        onViewCV={handleViewCV}
                        salaryRange={salaryRange}
                        niceToKnow={niceToKnow}
                      />
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Job Details & Conversation */}
          <div className={`${selectedJob ? 'flex' : 'hidden lg:flex'} lg:col-span-7 flex-col overflow-hidden min-h-0 h-full`}>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 flex flex-col overflow-hidden">
              {selectedJob ? (
                <>
                  {/* Job Header */}
                  <div className="px-3 py-2.5 sm:p-4 border-b border-slate-100 flex-shrink-0">
                    <div className="flex items-start gap-2.5">
                      <button
                        onClick={() => setSelectedJob(null)}
                        className="lg:hidden flex items-center justify-center w-8 h-8 rounded-full hover:bg-slate-100 transition-colors flex-shrink-0"
                      >
                        <ArrowLeft className="w-5 h-5 text-slate-600" />
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <h2 className="text-sm sm:text-lg font-bold tracking-tight text-slate-900 mb-1.5 truncate">
                          {selectedJob.metadata.job_title}
                        </h2>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium border border-slate-200 bg-white text-slate-600">
                            <span className={`size-1.5 rounded-full ${statusOptions.find(s => s.value === selectedJob.metadata.status)?.dot || 'bg-slate-300'}`} />
                            {statusOptions.find(s => s.value === selectedJob.metadata.status)?.label || selectedJob.metadata.status}
                          </span>
                          {selectedJob.metadata.conversation_id && salaryRangeByConversation[selectedJob.metadata.conversation_id] && (
                            <span className="text-[10px] sm:text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                              {salaryRangeByConversation[selectedJob.metadata.conversation_id]}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Messages - Scrollable */}
                  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 py-3 sm:p-4 space-y-3 relative min-h-0">
                    {selectedJob.metadata.conversation_id ? (
                      <div className="space-y-3">
                        {isLoadingMessages ? (
                          <div className="space-y-3 py-3 px-2">
                            {[0, 1, 2].map((i) => (
                              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[70%] rounded-xl p-3 relative overflow-hidden ${i % 2 === 0 ? 'bg-slate-100' : 'bg-slate-50'}`}>
                                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
                                  <div className="h-3 w-32 sm:w-44 bg-slate-200/60 rounded animate-pulse mb-1.5" />
                                  <div className="h-3 w-20 sm:w-28 bg-slate-200/40 rounded animate-pulse" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : conversationMessages.length > 0 ? (
                          conversationMessages.map((message) => {
                            const isSelf = message.sender.user_id === currentUser.id;
                            return (
                              <div
                                key={message.id}
                                className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}
                              >
                                <div
                                  className={`max-w-[80%] sm:max-w-xs lg:max-w-md px-3 py-2 sm:px-4 sm:py-2.5 rounded-2xl ${
                                    isSelf
                                      ? 'bg-gradient-to-br from-pink-500 to-rose-500 text-white'
                                      : 'bg-white border border-slate-200 text-slate-800'
                                  }`}
                                >
                                  <div 
                                    className="text-sm whitespace-pre-wrap"
                                    dangerouslySetInnerHTML={{ 
                                      __html: message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    }}
                                  />
                                  <div className={`text-[10px] mt-1 ${isSelf ? 'text-pink-200' : 'text-slate-400'}`}>
                                    {new Date(message.created_at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-8 sm:py-10 px-2">
                            <MessageCircle className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 sm:mb-3 text-slate-200" />
                            <p className="text-xs sm:text-sm text-slate-500 leading-snug">{t('loadingMessages')}</p>
                            <p className="text-[11px] sm:text-xs text-slate-400 mt-1 leading-snug">{t('loadingMessagesHint')}</p>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    ) : (
                      <div className="text-center py-10">
                        <MessageCircle className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                        <p className="text-sm text-slate-500">No conversation found</p>
                        <p className="text-xs text-slate-400">This reach-out may not have a conversation yet</p>
                      </div>
                    )}
                  </div>

                  {/* Message Input - Fixed at bottom */}
                  {selectedJob && selectedJob.metadata?.conversation_id && (
                    <div className="px-3 py-2.5 sm:p-4 border-t border-slate-100 bg-white flex-shrink-0">
                      <div className="flex gap-2 items-stretch relative">
                        <div className="flex-1 relative flex items-center">
                          <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder={t('chatWindow.inputPlaceholder')}
                            className="w-full min-h-[40px] px-3 py-2 pr-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 transition-colors resize-none"
                            maxLength={5000}
                            rows={1}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                              }
                              // Shift+Enter = new line (default behavior)
                            }}
                            autoFocus
                          />
                          
                          {/* Emoji Button */}
                          <button
                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Add emoji"
                          >
                            <Smile className="w-5 h-5 text-slate-500" />
                          </button>

                          {/* Emoji Picker */}
                          {showEmojiPicker && (
                            <div className="absolute bottom-full right-0 mb-2 z-50">
                              <EmojiPicker
                                onEmojiClick={handleEmojiClick}
                                width={350}
                                height={400}
                              />
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={sendMessage}
                          disabled={sendingMessage || !newMessage.trim()}
                          className="px-3 sm:px-4 min-h-[40px] bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl hover:shadow-md disabled:bg-slate-200 disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 self-end transition-all"
                        >
                          <Send className="w-4 h-4" />
                          <span className="hidden sm:inline text-sm font-medium">{sendingMessage ? '...' : 'Send'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                    <h3 className="text-base font-medium text-slate-700 mb-1">{t('emptyState.title')}</h3>
                    <p className="text-sm text-slate-500 mb-3">{t('emptyState.subtitle')}</p>
                    <p className="text-xs text-slate-400">{t('emptyState.hint')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CV Viewer Modal */}
      {showCVModal && selectedCandidateForCV && (
        <CVViewerModal
          cvId={selectedCandidateForCV.cvId}
          candidateName={selectedCandidateForCV.name}
          onClose={() => {
            setShowCVModal(false);
            setSelectedCandidateForCV(null);
          }}
        />
      )}
    </RecruiterPageShell>
  );
};

export default RecruiterInbox;
